import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { config } from "../config";
import { useCart } from "../contexts/CartContext";
import { useLocale } from "../contexts/LocaleContext";
import "./styles/thankyou.css";

/** Hash-router safe: `session_id` lives in the `#/path?…` segment, not always in `location.search`. */
function sessionIdFromWindowHash(): string | null {
  if (typeof window === "undefined") return null;
  const hash = window.location.hash || "";
  const q = hash.indexOf("?");
  if (q === -1) return null;
  return new URLSearchParams(hash.slice(q + 1)).get("session_id");
}

function sessionIdFromLocationSearch(): string | null {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get("session_id");
}

function isPaidStripeStatus(s: string): boolean {
  return (
    s === "paid" ||
    s === "succeeded" ||
    s === "success" ||
    s === "complete" ||
    s === "no_payment_required"
  );
}

// Polls /api/v1/stripe/status until the backend confirms (or rejects) the
// payment, then shows the thank-you screen.
export function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const { clear: clearCart } = useCart();
  const { t } = useLocale();

  const sessionId = useMemo(() => {
    return (
      params.get("session_id") ||
      sessionIdFromWindowHash() ||
      sessionIdFromLocationSearch()
    );
  }, [params]);

  const [status, setStatus] = useState<"pending" | "paid" | "failed" | "none">(
    sessionId ? "pending" : "none",
  );
  const [message, setMessage] = useState<string>(
    sessionId ? t("paymentSuccess.checking") : t("paymentSuccess.noPayment"),
  );
  const cancelled = useRef(false);
  const clearedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) return;
    cancelled.current = false;
    clearedRef.current = false;

    const checkOnce = async (): Promise<boolean> => {
      try {
        const resp = await fetch(
          `${config.apiUrl}/stripe/status?session_id=${encodeURIComponent(sessionId)}`,
          { credentials: "include", headers: { Accept: "application/json" } },
        );
        if (!resp.ok) {
          setMessage(
            t("paymentSuccess.statusError", { status: resp.status }),
          );
          setStatus("failed");
          return true;
        }
        const data = (await resp.json()) as { status?: string; error?: string };
        const s = (data.status || "").toLowerCase();
        if (isPaidStripeStatus(s)) {
          if (!clearedRef.current) {
            clearedRef.current = true;
            try {
              await clearCart();
            } catch (e) {
              console.error("PaymentSuccess: cart clear failed", e);
              clearedRef.current = false;
            }
          }
          setStatus("paid");
          return true;
        }
        if (s === "pending" || s === "processing" || s === "requires_action") {
          setMessage(t("paymentSuccess.stillPending"));
          return false;
        }
        if (s === "canceled" || s === "cancelled") {
          setMessage(t("paymentSuccess.cancelled"));
          setStatus("failed");
          return true;
        }
        if (
          s === "error" ||
          s === "failed" ||
          s === "expired" ||
          s === "unpaid"
        ) {
          setMessage(
            t("paymentSuccess.paymentError", {
              error: data.error || t("paymentSuccess.paymentErrorGeneric"),
            }),
          );
          setStatus("failed");
          return true;
        }
        setMessage(t("paymentSuccess.unknownStatus", { status: s || "—" }));
        setStatus("failed");
        return true;
      } catch {
        setMessage(t("paymentSuccess.checkFailed"));
        setStatus("failed");
        return true;
      }
    };

    const loop = async () => {
      while (!cancelled.current) {
        const done = await checkOnce();
        if (done) return;
        await new Promise((r) => setTimeout(r, 5000));
      }
    };
    void loop();
    return () => {
      cancelled.current = true;
    };
  }, [sessionId, clearCart, t]);

  if (status === "paid") {
    return (
      <section
        className="wrapper thankyou-page"
        aria-label={t("paymentSuccess.ariaLabel")}
      >
        <div className="thankyou-box">
          <div className="thankyou-graphic">
            <img
              src="/static/img/Heartfelt-Thank-You.png"
              alt={t("paymentSuccess.thankYouAlt")}
            />
          </div>
          <h1>{t("paymentSuccess.thankYouTitle")}</h1>
          <p>
            {t("paymentSuccess.thankYouText")}
            <br />
            {t("paymentSuccess.thankYouProfile")}
          </p>
          <div className="thankyou-action">
            <Link to="/profile" className="btn-profile">
              {t("paymentSuccess.toProfile")}
            </Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <div className="content">
      <div className="framed">
        <h1>{t("paymentSuccess.title")}</h1>
        <p>{message}</p>
      </div>
    </div>
  );
}
